import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, TypedUseSelectorHook, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  InputAdornment,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Stack,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import { FaCamera, FaFileExcel, FaTimes, FaSave, FaEdit, FaUndo } from 'react-icons/fa';
import { MdBusiness } from 'react-icons/md';
import {
  createOrganization,
  fetchOrganization,
  clearOrganizationState,
  OrganizationFormData,
  Organization,
  FileData
} from 'slices/NdisSlices/carporateServices/organizationSlice';
import { AppDispatch, RootState } from 'app/store';
import { useNavigate } from 'react-router-dom';

// Australian states static array
const AUSTRALIAN_STATES = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'SA', name: 'South Australia' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'ACT', name: 'Australian Capital Territory' }
];

// Drag and drop component
const FileDropzone: React.FC<{
  onFileChange: (file: File) => void;
  fileName: string | null;
  onRemove: () => void;
  error?: string;
}> = ({ onFileChange, fileName, onRemove, error }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (
          file.type.includes('excel') ||
          file.type.includes('spreadsheet') ||
          file.name.match(/\.(xlsx|xls|csv)$/)
        ) {
          onFileChange(file);
        }
      }
    },
    [onFileChange]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFileChange(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box
      onClick={handleClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: isDragging ? theme.palette.action.hover : theme.palette.background.paper,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.action.hover
        }
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
      />
      {fileName ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <FaFileExcel size={24} color={theme.palette.success.main} />
          <Typography variant="body1">{fileName}</Typography>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{ color: theme.palette.error.main }}
          >
            <FaTimes />
          </IconButton>
        </Box>
      ) : (
        <>
          <FaFileExcel size={48} color={theme.palette.primary.main} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Drag & drop your Excel file here
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
            or click to browse files
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
            Supported formats: .xlsx, .xls, .csv
          </Typography>
        </>
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

// Validation schema
// Validation schema
const schema = yup
  .object({
    business_name: yup.string().required('Business name is required'),
    abn: yup
      .string()
      .required('ABN is required')
      .matches(/^\d{11}$/, 'ABN must be 11 digits'),
    bsb: yup
      .string()
      .required('BSB is required')
      .matches(/^\d{6}$/, 'BSB must be 6 digits'),
    account_number: yup.string().required('Account number is required'),
    account_name: yup.string().required('Account name is required'),
    business_email: yup.string().email('Invalid email').required('Business email is required'),
    smtp_server: yup.string().required('SMTP server is required'),
    smtp_port: yup.string().required('SMTP port is required'),
    smtp_username: yup.string().required('SMTP username is required'),
    smtp_password: yup.string().required('SMTP password is required'),
    business_phone: yup.string().required('Business phone is required'),
    business_address: yup.string().required('Business address is required'),
    state: yup
      .string()
      .required('State is required')
      .oneOf(
        AUSTRALIAN_STATES.map((state) => state.code),
        'Invalid state'
      ),
    website: yup.string().url('Invalid URL').optional().default(''),
    logo: yup.mixed<File>().optional().default(undefined),
    support_items_file: yup.mixed<File>().optional().default(undefined),
    // New fields
    Gst_code: yup.string().required('GST code is required'),
    In_kind_finding_program: yup.string().required('In-kind funding program is required'),
    registration_number: yup.string().required('Registration number is required'),
    Participant_approved: yup.boolean().required('Participant approval status is required')
  })
  .required();

const OrganizationView: React.FC<{
  organization: Organization;
  onEdit: () => void;
}> = ({ organization, onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper
      elevation={isMobile ? 1 : 4}
      sx={{
        p: isMobile ? 2 : 4,
        borderRadius: 2,
        boxShadow: isMobile ? theme.shadows[1] : theme.shadows[4]
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={
              organization.icon_path ? `data:image/png;base64,${organization.icon_path}` : undefined
            }
            variant="rounded"
            sx={{ width: 60, height: 60, bgcolor: theme.palette.primary.main }}
          >
            <MdBusiness size={24} />
          </Avatar>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
            {organization.business_name}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FaEdit />}
          onClick={onEdit}
          sx={{
            borderRadius: 2,
            borderColor:
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
            color: theme.palette.text.primary,
            textTransform: 'none',
            px: 2,
            py: 1,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover
            },
            ...(isMobile && {
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5
            })
          }}
        >
          Edit
        </Button>
      </Box>

      <Grid container spacing={isMobile ? 1 : 3}>
        {/* Business Information */}
        <Grid item xs={12} md={6}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{ mb: 2, color: theme.palette.primary.main }}
          >
            Business Information
          </Typography>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Business Name
              </Typography>
              <Typography>{organization.business_name}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                ABN
              </Typography>
              <Typography>{organization.abn}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Business Address
              </Typography>
              <Typography>{organization.business_address}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                State
              </Typography>
              <Typography>
                {AUSTRALIAN_STATES.find((s) => s.code === organization.state)?.name ||
                  organization.state}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Website
              </Typography>
              <Typography>{organization.website || 'Not provided'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Business Phone
              </Typography>
              <Typography>{organization.business_phone}</Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Banking Information */}
        <Grid item xs={12} md={6}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{ mb: 2, color: theme.palette.primary.main }}
          >
            Banking Information
          </Typography>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                BSB
              </Typography>
              <Typography>{organization.bsb}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Account Number
              </Typography>
              <Typography>{organization.account_number}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Account Name
              </Typography>
              <Typography>{organization.account_name}</Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Email Configuration */}
        <Grid item xs={12}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{ mb: 2, color: theme.palette.primary.main }}
          >
            Email Configuration
          </Typography>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Grid container spacing={isMobile ? 1 : 3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Business Email
                </Typography>
                <Typography>{organization.business_email}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  SMTP Server
                </Typography>
                <Typography>{organization.smtp_server}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  SMTP Port
                </Typography>
                <Typography>{organization.smtp_port}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  SMTP Username
                </Typography>
                <Typography>{organization.smtp_username}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{ mb: 2, color: theme.palette.primary.main }}
          >
            Additional Information
          </Typography>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                GST Code
              </Typography>
              <Typography>{organization.Gst_code}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                In-kind Funding Program
              </Typography>
              <Typography>{organization.In_kind_finding_program}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Registration Number
              </Typography>
              <Typography>{organization.registration_number}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Participant Approved
              </Typography>
              <Typography>
                {organization.Participant_approved ? 'Approved' : 'Not Approved'}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Support Items Data */}
        <Grid item xs={12}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{ mb: 2, color: theme.palette.primary.main }}
          >
            Support Items Data
          </Typography>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Uploaded File
            </Typography>
            {organization.files?.find((f: FileData) => f.file_name.includes('.xlsx')) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <FaFileExcel size={20} color={theme.palette.success.main} />
                <Typography
                  component="a"
                  href={
                    organization.files.find((f: FileData) => f.file_name.includes('.xlsx'))?.sas_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {
                    organization.files.find((f: FileData) => f.file_name.includes('.xlsx'))
                      ?.file_name
                  }
                </Typography>
              </Box>
            ) : (
              <Typography>No file uploaded</Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const OrganizationForm: React.FC<{
  defaultValues: Organization;
  onSubmit: (data: OrganizationFormData) => void;
  onCancel: () => void;
}> = ({ defaultValues, onSubmit, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
  const { message, loading, error } = useTypedSelector((state) => state.organization);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    defaultValues.icon_path ? `data:image/png;base64,${defaultValues.icon_path}` : null
  );
  const [supportItemsFileName, setSupportItemsFileName] = useState<string | null>(
    defaultValues.files?.find((f: FileData) => f.file_name.includes('.xlsx'))?.file_name || null
  );
  const logoInputRef = useRef<HTMLInputElement>(null);
  const supportItemsInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty }
  } = useForm<OrganizationFormData>({
    resolver: yupResolver<OrganizationFormData>(schema),
    defaultValues: {
      business_name: defaultValues.business_name,
      abn: defaultValues.abn,
      bsb: defaultValues.bsb,
      account_number: defaultValues.account_number,
      account_name: defaultValues.account_name,
      business_email: defaultValues.business_email,
      smtp_server: defaultValues.smtp_server,
      smtp_port: defaultValues.smtp_port,
      smtp_username: defaultValues.smtp_username,
      smtp_password: defaultValues.smtp_password,
      business_phone: defaultValues.business_phone,
      business_address: defaultValues.business_address,
      state: defaultValues.state || AUSTRALIAN_STATES[0].code,
      website: defaultValues.website,
      logo: undefined,
      support_items_file: undefined,
      Gst_code: defaultValues.Gst_code || '',
      In_kind_finding_program: defaultValues.In_kind_finding_program || '',
      registration_number: defaultValues.registration_number || '',
      Participant_approved: defaultValues.Participant_approved || false
    }
  });

  const handleLogoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: File | undefined) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
      onChange(undefined);
    }
  };

  const handleSupportItemsChange = (file: File) => {
    setValue('support_items_file', file, { shouldDirty: true });
    setSupportItemsFileName(file.name);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setValue('logo', undefined, { shouldDirty: true });
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const removeSupportItemsFile = () => {
    setSupportItemsFileName(null);
    setValue('support_items_file', undefined, { shouldDirty: true });
  };

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
    setLogoPreview(
      defaultValues.icon_path ? `data:image/png;base64,${defaultValues.icon_path}` : null
    );
    setSupportItemsFileName(
      defaultValues.files?.find((f: FileData) => f.file_name.includes('.xlsx'))?.file_name || null
    );
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
    dispatch(clearOrganizationState());
  };

  const handleFormSubmit = (data: OrganizationFormData) => {
    dispatch(createOrganization(data))
      .unwrap()
      .then(() => {
        onSubmit(data);
        reset();
        setLogoPreview(null);
        setSupportItemsFileName(null);
        if (logoInputRef.current) {
          logoInputRef.current.value = '';
        }
        setTimeout(() => dispatch(clearOrganizationState()), 5000);
      });
  };

  return (
    <Paper
      elevation={isMobile ? 1 : 4}
      sx={{
        p: isMobile ? 2 : 4,
        borderRadius: 2,
        boxShadow: isMobile ? theme.shadows[1] : theme.shadows[4]
      }}
    >
      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          mb: isMobile ? 2 : 4,
          px: isMobile ? 1 : 0
        }}
      >
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          component="h1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.2,
            color: theme.palette.text.primary
          }}
        >
          Edit Organization
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearOrganizationState())}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearOrganizationState())}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {/* Logo Upload */}
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMobile ? 1 : 2,
              pl: 1
            }}
          >
            <MdBusiness
              size={isMobile ? 16 : 20}
              style={{
                marginRight: 8,
                color: theme.palette.primary.main
              }}
            />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? '1.1rem' : '1.5rem'
              }}
            >
              Organization Logo
            </Typography>
          </Box>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: 2,
              width: '100%'
            }}
          >
            <Avatar
              src={logoPreview || undefined}
              variant="rounded"
              sx={{
                width: isMobile ? '100%' : 150,
                height: 150,
                bgcolor: theme.palette.grey[200],
                objectFit: 'contain',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: isMobile ? 'auto' : 0
              }}
            >
              {!logoPreview && <MdBusiness size={32} />}
            </Avatar>

            <Controller
              name="logo"
              control={control}
              render={({ field: { onChange, onBlur, ...field } }) => (
                <Box sx={{ width: isMobile ? '100%' : 'auto', flex: 1 }}>
                  <TextField
                    size="small"
                    label="Upload Logo"
                    type="file"
                    fullWidth
                    margin="normal"
                    inputRef={logoInputRef}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaCamera size={16} />
                        </InputAdornment>
                      ),
                      inputProps: {
                        accept: 'image/*'
                      }
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleLogoChange(e, onChange)
                    }
                    onBlur={onBlur}
                    error={!!errors.logo}
                    helperText={errors.logo?.message as string}
                  />

                  {logoPreview && (
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={removeLogo}
                      startIcon={<FaTimes />}
                      sx={{ mt: 1 }}
                    >
                      Remove Logo
                    </Button>
                  )}
                </Box>
              )}
            />
          </Box>
        </Box>

        {/* Business Details */}
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMobile ? 1 : 2,
              pl: 1
            }}
          >
            <MdBusiness
              size={isMobile ? 16 : 20}
              style={{
                marginRight: 8,
                color: theme.palette.primary.main
              }}
            />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? '1.1rem' : '1.5rem'
              }}
            >
              Business Details
            </Typography>
          </Box>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="business_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Business Name"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., Ultra Care Services"
                    {...field}
                    error={!!errors.business_name}
                    helperText={errors.business_name?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="abn"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="ABN"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., 12345678901"
                    {...field}
                    error={!!errors.abn}
                    helperText={errors.abn?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="business_address"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Business Address"
                    multiline
                    rows={2}
                    fullWidth
                    margin="normal"
                    placeholder="e.g., 123 Care Street, Sydney NSW 2000"
                    {...field}
                    error={!!errors.business_address}
                    helperText={errors.business_address?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="state"
                control={control}
                render={({ field: { onChange, value, onBlur, ...field } }) => (
                  <Box>
                    <Select
                      size="small"
                      value={value}
                      onChange={(e: SelectChangeEvent<string>) => onChange(e.target.value)}
                      onBlur={onBlur}
                      fullWidth
                      error={!!errors.state}
                      sx={{ mt: 2 }}
                      {...field}
                    >
                      {AUSTRALIAN_STATES.map((state) => (
                        <MenuItem key={state.code} value={state.code}>
                          {state.name} ({state.code})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.state && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errors.state.message as string}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="website"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Website"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., https://example.com"
                    {...field}
                    error={!!errors.website}
                    helperText={errors.website?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="business_phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Business Phone"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., 0412345678"
                    {...field}
                    error={!!errors.business_phone}
                    helperText={errors.business_phone?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="business_email"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Business Email"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., info@ultracare.com"
                    {...field}
                    error={!!errors.business_email}
                    helperText={errors.business_email?.message as string}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Banking Details */}
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMobile ? 1 : 2,
              pl: 1
            }}
          >
            <MdBusiness
              size={isMobile ? 16 : 20}
              style={{
                marginRight: 8,
                color: theme.palette.primary.main
              }}
            />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? '1.1rem' : '1.5rem'
              }}
            >
              Banking Details
            </Typography>
          </Box>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="bsb"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="BSB"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., 123456"
                    {...field}
                    error={!!errors.bsb}
                    helperText={errors.bsb?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="account_number"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Account Number"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., 987654321"
                    {...field}
                    error={!!errors.account_number}
                    helperText={errors.account_number?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="account_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Account Name"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., Ultra Care Services"
                    {...field}
                    error={!!errors.account_name}
                    helperText={errors.account_name?.message as string}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Email Configuration */}
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMobile ? 1 : 2,
              pl: 1
            }}
          >
            <MdBusiness
              size={isMobile ? 16 : 20}
              style={{
                marginRight: 8,
                color: theme.palette.primary.main
              }}
            />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? '1.1rem' : '1.5rem'
              }}
            >
              Email Configuration
            </Typography>
          </Box>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="smtp_server"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="SMTP Server"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., smtp.ultracare.com"
                    {...field}
                    error={!!errors.smtp_server}
                    helperText={errors.smtp_server?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="smtp_port"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="SMTP Port"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., 587"
                    {...field}
                    error={!!errors.smtp_port}
                    helperText={errors.smtp_port?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="smtp_username"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="SMTP Username"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., info@ultracare.com"
                    {...field}
                    error={!!errors.smtp_username}
                    helperText={errors.smtp_username?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="smtp_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="SMTP Password"
                    type="password"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., your-smtp-password"
                    {...field}
                    error={!!errors.smtp_password}
                    helperText={errors.smtp_password?.message as string}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Additional Information */}
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMobile ? 1 : 2,
              pl: 1
            }}
          >
            <MdBusiness
              size={isMobile ? 16 : 20}
              style={{
                marginRight: 8,
                color: theme.palette.primary.main
              }}
            />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? '1.1rem' : '1.5rem'
              }}
            >
              Additional Information
            </Typography>
          </Box>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="Gst_code"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="GST Code"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., GST123456"
                    {...field}
                    error={!!errors.Gst_code}
                    helperText={errors.Gst_code?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="In_kind_finding_program"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="In-kind Funding Program"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., Community Support Program"
                    {...field}
                    error={!!errors.In_kind_finding_program}
                    helperText={errors.In_kind_finding_program?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="registration_number"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Registration Number"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., REG789012"
                    {...field}
                    error={!!errors.registration_number}
                    helperText={errors.registration_number?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="Participant_approved"
                control={control}
                render={({ field: { onChange, value, onBlur, ...field } }) => (
                  <Box>
                    <Select
                      size="small"
                      value={value ? 'true' : 'false'}
                      onChange={(e: SelectChangeEvent<string>) =>
                        onChange(e.target.value === 'true')
                      }
                      onBlur={onBlur}
                      fullWidth
                      error={!!errors.Participant_approved}
                      sx={{ mt: 2 }}
                      {...field}
                    >
                      <MenuItem value="true">Approved</MenuItem>
                      <MenuItem value="false">Not Approved</MenuItem>
                    </Select>
                    {errors.Participant_approved && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errors.Participant_approved.message as string}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Support Items Data */}
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMobile ? 1 : 2,
              pl: 1
            }}
          >
            <FaFileExcel
              size={isMobile ? 16 : 20}
              style={{
                marginRight: 8,
                color: theme.palette.primary.main
              }}
            />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? '1.1rem' : '1.5rem'
              }}
            >
              Support Items Data
            </Typography>
          </Box>
          <Divider sx={{ mb: isMobile ? 2 : 3 }} />
          <Controller
            name="support_items_file"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <FileDropzone
                onFileChange={handleSupportItemsChange}
                fileName={supportItemsFileName}
                onRemove={removeSupportItemsFile}
                error={errors.support_items_file?.message as string}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Upload an Excel file containing support items data
          </Typography>
        </Box>

        {/* Submit and Reset Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: isMobile ? 1.5 : 2,
            mt: isMobile ? 2 : 3,
            mb: isMobile ? 1 : 0,
            flexDirection: isMobile ? 'column' : 'row'
          }}
        >
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            size="small"
            sx={{
              px: 2,
              fontSize: '0.8rem',
              fontWeight: 500,
              borderRadius: 1.5,
              width: isMobile ? '100%' : 'auto',
              minHeight: 36,
              boxShadow: 0,
              '&:hover': {
                boxShadow: 1,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}
            startIcon={<FaTimes size={14} />}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="outlined"
            color="secondary"
            size="small"
            sx={{
              px: 2,
              fontSize: '0.8rem',
              fontWeight: 500,
              borderRadius: 1.5,
              width: isMobile ? '100%' : 'auto',
              minHeight: 36,
              boxShadow: 0,
              '&:hover': {
                boxShadow: 1,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}
            startIcon={<FaUndo size={14} />}
            onClick={handleReset}
            disabled={!isDirty || loading}
          >
            Reset
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            sx={{
              px: 2.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              borderRadius: 1.5,
              width: isMobile ? '100%' : 'auto',
              minHeight: 36,
              boxShadow: 0,
              '&:hover': {
                boxShadow: 2,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}
            startIcon={<FaSave size={14} />}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

const OrganizationManagement: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
  const { organization, loading, error } = useTypedSelector((state) => state.organization);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const project = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    const service = JSON.parse(localStorage.getItem('servicesState') || '{}') as {
      selectedServiceTitle?: string;
    };
    const group_id = project.projectId || '';
    const service_name_id = service.selectedServiceTitle || '';

    if (group_id && service_name_id) {
      dispatch(fetchOrganization({ group_id, service_name_id }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [dispatch]);

  const handleSubmit = (data: OrganizationFormData) => {
    dispatch(createOrganization(data))
      .unwrap()
      .then(() => {
        setIsEditing(false);
        dispatch(
          fetchOrganization({
            group_id: JSON.parse(localStorage.getItem('selectedProject') || '{}').projectId || '',
            service_name_id:
              JSON.parse(localStorage.getItem('servicesState') || '{}').selectedServiceTitle || ''
          })
        );
      });
  };

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearOrganizationState())}>
          {error}
        </Alert>
      )}
      {organization && !loading && (
        <>
          {isEditing ? (
            <OrganizationForm
              defaultValues={organization}
              onSubmit={handleSubmit}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <OrganizationView organization={organization} onEdit={() => setIsEditing(true)} />
          )}
        </>
      )}
    </Box>
  );
};

export default OrganizationManagement;
